import { useCallback, useMemo } from 'react';

import { useNovel } from '@/context/novel-context';
import { useEntity } from '@/hooks/use-entity';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { AnySummary, EntitySummary, EntityType, EventSummary, SectionDefinition } from '@/lib/types';

import { useFirebase } from '@/firebase';
import { deleteProcessedImage } from '@/firebase/storage';
import { useEntitySettings } from './use-entity-settings';

/**
 * Return type for the generic entity access layer.
 */
export interface EntityAccessLayer<TItem, TSummary extends AnySummary> {
  /** Summary list for the entity type (derived from novel metadata). */
  summaries: TSummary[];
  /** The specific item if itemId was provided. */
  item: TItem | null;
  /** UI settings for this entity type. */
  sections: SectionDefinition[];
  /** Aggregate loading state. */
  isLoading: boolean;
  /** Aggregate error state. */
  error: Error | null;
  /**
   * Partial-update an entity and auto-sync the summary
   * when any summary-affecting field is changed.
   */
  updateItem: (id: string, partial: Record<string, unknown>) => Promise<void>;
  /** Delete an entity and remove its summary. */
  deleteItem: (id: string) => Promise<void>;
  /** Generate a new Firestore-compatible ID. */
  getNewId: () => string;
}

type SortFn<T> = (a: T, b: T) => number;

interface EntityAccessLayerOptions<TItem, TSummary extends AnySummary> {
  /** The entity type key, e.g. 'characters', 'locations'. */
  entityType: EntityType;
  /**
   * Field names that, when present in a partial update,
   * should trigger an automatic summary sync.
   */
  summaryFieldKeys: readonly string[];
  /** Optional ID of a specific item to fetch. */
  itemId?: string;
  /** Optional mapper to transform the raw Firestore DTO to domain model. */
  itemMapper?: (dto: any) => TItem;
  /** Optional custom sort for the summary list. Defaults to updatedAt DESC. */
  sort?: SortFn<TSummary>;
  /** Optional override for the settings key. Useful for dynamic sub-types like 'codex:category'. */
  settingsType?: string;
}

import { toTimestamp } from '@/lib/date-utils';

const DEFAULT_SORT: SortFn<AnySummary> = (a, b) => {
  const timeA = toTimestamp(a.updatedAt);
  const timeB = toTimestamp(b.updatedAt);
  return timeB - timeA;
};

/**
 * Generic factory hook that centralises reads + writes for any entity type.
 *
 * **Reads** come from:
 * 1. `novel.summaries` (fast list)
 * 2. `useEntity` (single document fetch if itemId provided)
 * 3. `useEntitySettings` (user-defined fields/sections)
 *
 * **Writes** go through `useNovelRepository` and auto-sync the summary
 * when the changed fields overlap with `summaryFieldKeys`.
 */
export function useEntityAccessLayer<
  TItem = any,
  TSummary extends EntitySummary | EventSummary = EntitySummary,
>(options: EntityAccessLayerOptions<TItem, TSummary>): EntityAccessLayer<TItem, TSummary> {
  const { entityType, itemId, itemMapper, sort, settingsType } = options;

  // 1. Core Data
  const { novel, isLoading: isLoadingNovel, error: errorNovel, isExampleNovel } = useNovel();
  const { storage } = useFirebase();
  const {
    entityDao,
    getNewId,
  } = useNovelRepository();

  // 2. Single Entity Read (Conditional based on itemId)
  const { data: rawItem, isLoading: isLoadingItem, error: errorItem } = useEntity<any>(
    entityType,
    itemId,
  );

  const item = useMemo(() => {
    if (!rawItem) return null;
    return itemMapper ? itemMapper(rawItem) : (rawItem as TItem);
  }, [rawItem, itemMapper]);

  // 3. Settings Read
  const effectiveSettingsType = useMemo(() => {
    if (settingsType) return settingsType;
    if (entityType === 'codex' && rawItem?.type) return `codex:${rawItem.type}`;
    return entityType;
  }, [settingsType, entityType, rawItem?.type]);

  const { sections, isLoading: isLoadingSettings, error: errorSettings } = useEntitySettings(effectiveSettingsType);

  // 4. Derived summary list
  const summaries = useMemo(() => {
    const map = novel.summaries[entityType];
    if (!map) return [];

    const list = Object.values(map) as TSummary[];
    const comparator = (sort ?? DEFAULT_SORT) as SortFn<TSummary>;
    return [...list].sort(comparator);
  }, [novel, entityType, sort]);

  // Aggregate states
  const isLoading = isLoadingNovel || isLoadingItem || isLoadingSettings;
  const error = errorNovel || errorItem || errorSettings;

  // ── Mutations ───────────────────────────────────────────────────

  const updateItem = useCallback(
    async (id: string, partial: Record<string, unknown>) => {
      if (!novel.id || !entityDao) return;

      // The DAO handles both the item update and the summary mirroring
      // based on standard SUMMARY_FIELDS.
      await entityDao.updateEntity(novel.ownerId, novel, entityType, id, partial);
    },
    [novel, entityType, entityDao],
  );

  const deleteItemFn = useCallback(
    async (id: string) => {
      if (!novel.id || !entityDao) return;

      // Clean up storage images if any
      if (storage) {
        try {
          const fetchedItem = await entityDao.getItem<{ images?: any[] }>(
            novel.ownerId,
            novel,
            'entities',
            entityType,
            id
          );
          if (fetchedItem?.images && fetchedItem.images.length > 0) {
            await Promise.all(
              fetchedItem.images.map((img) => deleteProcessedImage(storage, img))
            );
          }
        } catch (err) {
          console.warn(`Failed to clean up storage images for deleted ${entityType}:`, err);
        }
      }

      await entityDao.deleteEntity(novel.ownerId, novel, entityType, id);
    },
    [novel, entityType, entityDao, storage],
  );

  return {
    summaries,
    item,
    sections,
    isLoading,
    error,
    updateItem,
    deleteItem: deleteItemFn,
    getNewId,
  };
}
